/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { AmplitudeService } from '@inyova/inyova-shared';
import { Customer, CustomerAccount } from '@inyova/models';
import { isCustomer } from '@inyova/utils';
import { TranslateService } from '@ngx-translate/core';
import * as d3 from 'd3';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as fromAppRoot from '@app/app.reducers';

import { INVESTMENT_LIMITS } from '@app/app.constants';
import { SavingsPlanSocialProof } from '@shared/models/CustomerAccount';
import { EstimatedValues, StackedAreaChartData, StackedAreaChartDataItem } from '@shared/models/Reporting';
import { CustomerInvestmentData } from '@shared/models/Strategy';
import { ToastNotificationService } from '@shared/services/toast-notification.service';
import { TrackingService } from '@shared/services/tracking.service';

import { getAggregatedValues, getExpectedValues } from './savings-analysis.utils';

@Component({
  selector: 'app-savings-analysis',
  templateUrl: './savings-analysis.component.html',
  styleUrls: ['./savings-analysis.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class SavingsAnalysisComponent implements OnInit, OnChanges, OnDestroy {
  private readonly onDestroy$ = new Subject<void>();
  @ViewChild('chartContainer', { static: true }) chartContainer: ElementRef;
  @Input() customerInvestment: CustomerInvestmentData;
  @Input() customer: Customer;
  @Input() currentAccount: CustomerAccount;
  @Input() hideSavingsPlan = false;
  @Output() changeTab = new EventEmitter<number>();

  config = {
    height: 400,
    margin: {
      top: 24,
      bottom: 24,
      left: 0,
      right: 0
    }
  };

  chartData: StackedAreaChartData = { columns: [], data: [] };
  currentChip = 0;
  estimatedPeriod = 20;
  estimatedValues: EstimatedValues;
  expectedPercentages: { loss: number; expected: number; good: number };
  newMonthlyValue: number;
  riskLevel: string;
  savingsPlanSocialProof: SavingsPlanSocialProof;
  showDetails = false;

  tooltipData = {
    expected: 0,
    potential: 0,
    loss: 0
  };

  // D3
  private svg: any;
  private area: any;
  private lines: any;

  constructor(
    private container: ElementRef,
    private store: Store,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private toastNotificationService: ToastNotificationService,
    private amplitudeService: AmplitudeService
  ) {
    this.store.pipe(select(fromAppRoot.selectSavingsPlanSocialProof), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.savingsPlanSocialProof = value;
    });
  }

  ngOnInit() {
    this.calculateChartData(20);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.customerInvestment) {
      this.newMonthlyValue = this.customerInvestment.monthly_investment;
      this.calculateChartData(20);
      this.drawChart();
    }
  }

  calculateChartData(estimatedPeriod: number) {
    this.estimatedPeriod = estimatedPeriod;
    this.chartData = { columns: [], data: [] }; // clear data

    const expectedValues = getExpectedValues(this.customer.app_location, this.customerInvestment.stock_split);
    this.expectedPercentages = {
      loss: expectedValues.expectedLossRatio,
      expected: expectedValues.expectedReturnRatio,
      good: expectedValues.expectedPotentialRatio
    };
    const { expectedReturnRatioFinal, expectedSTDDevRatioFinal, expectedMonthlyReturn, confidenceInterval } = expectedValues;

    const currentYear = new Date().getFullYear();
    const total = this.customerInvestment?.current_amount;
    const monthly = this.newMonthlyValue;
    let NAV = total;
    let NAVWithout = total;
    const firstYearData = { date: new Date(`01/01/${currentYear.toFixed()}`) } as StackedAreaChartDataItem;
    firstYearData['badWithout'] = total; // We initialize just the lowest line (stacked chart)
    firstYearData['expectedWithout'] = 0;
    firstYearData['goodWithout'] = 0;
    firstYearData['bad'] = 0;
    firstYearData['expected'] = 0;
    firstYearData['good'] = 0;
    this.chartData.data.push(firstYearData);

    for (let i = 1; i <= estimatedPeriod * 12; i++) {
      const aggregatedValues = getAggregatedValues(
        i,
        expectedReturnRatioFinal,
        expectedSTDDevRatioFinal,
        confidenceInterval,
        expectedMonthlyReturn,
        NAV,
        NAVWithout,
        monthly
      );

      const { WCNav, WCNavWithout, BCNav, BCNavWithout } = aggregatedValues;
      NAV = aggregatedValues.NAV;
      NAVWithout = aggregatedValues.NAVWithout;

      if (i % 12 === 0) {
        const newYearData = { date: new Date(`01/01/${(currentYear + i / 12).toFixed()}`) } as StackedAreaChartDataItem;
        // We add just differance between the last and new line (stacked chart)
        newYearData['badWithout'] = parseFloat(WCNavWithout.toFixed(0));
        newYearData['expectedWithout'] = parseFloat(NAVWithout.toFixed(0)) - parseFloat(WCNavWithout.toFixed(0));
        newYearData['goodWithout'] = parseFloat(BCNavWithout.toFixed(0)) - parseFloat(NAVWithout.toFixed(0));
        newYearData['bad'] = parseFloat(WCNav.toFixed(0)) - parseFloat(BCNavWithout.toFixed(0));
        newYearData['expected'] = parseFloat(NAV.toFixed(0)) - parseFloat(WCNav.toFixed(0));
        newYearData['good'] = parseFloat(BCNav.toFixed(0)) - parseFloat(NAV.toFixed(0));
        this.chartData.data.push(newYearData);
      }

      this.estimatedValues = {
        badWithout: WCNavWithout,
        expectedWithout: NAVWithout,
        goodWithout: BCNavWithout,
        bad: WCNav,
        expected: NAV,
        good: BCNav
      };
    }
    this.chartData.columns = ['date', 'badWithout', 'expectedWithout', 'goodWithout', 'bad', 'expected', 'good'];
    this.trackingService.trackActivity(`[Select] Estimated value for ${estimatedPeriod} years`);
    this.drawChart();
  }

  amplitudeTrack(event: string, data?: any) {
    this.amplitudeService.trackEvent(event, data);
  }

  drawChart() {
    // Size
    const data = this.chartData;
    const height = this.config.height - this.config.margin.top - this.config.margin.bottom;
    const width = this.chartContainer.nativeElement.offsetWidth;
    // Format data
    const series = d3.stack().keys(data.columns.slice(1))(data.data as any);
    // Select main SVG
    this.svg = d3.select(this.container.nativeElement).select('svg').attr('width', width).attr('height', height);

    // Clear graph on update
    this.svg.select('.grid').remove();
    this.svg.select('.stacked-area-paths').remove();
    this.svg.select('.stacked-area-lines').remove();
    this.svg.select('.yaxis-stacked').remove();
    this.svg.selectAll('path').remove();

    // X
    const x = d3
      .scaleUtc()
      .domain(d3.extent(data.data, (d: StackedAreaChartDataItem) => d.date))
      .range([this.config.margin.left, width - this.config.margin.right]);

    // Y
    const y = d3
      .scaleLinear()
      .domain([0, d3.max(series, (d) => d3.max(d, (d) => d[1]))])
      .nice()
      .range([height - this.config.margin.bottom, this.config.margin.top]);

    // Create line
    this.area = d3
      .area()
      .x((d: any) => x(d.data.date))
      .y0((d) => y(d[0]))
      .y1((d) => y(d[1]));

    // Create line
    const line = d3
      .line()
      .x((d: any) => x(d.data.date))
      .y((d: any) => y(d[1]));

    // Grid
    const grid = (g) =>
      g
        .attr('transform', `translate(30,0)`)
        .attr('class', 'grid')
        .call(
          d3
            .axisLeft(y)
            .ticks(height / 50)
            .tickSize(30 - width)
            .tickFormat(() => ' ')
        );

    // Y axis
    const yAxis = (g) =>
      g
        .attr('transform', `translate(9,0)`)
        .call(
          d3
            .axisLeft(y)
            .ticks(height / 50)
            .tickSizeOuter(0)
            .tickFormat((d) => {
              const format = d3.format('.2s')(d);
              return format.replace('.0', '');
            })
        )
        .call((g) => g.select('.domain').remove());

    // Create grid
    this.svg.append('g').attr('class', 'grid').call(grid);

    // Create lines
    this.lines = this.svg
      .append('g')
      .attr('class', 'stacked-area-lines')
      .selectAll('path')
      .data(series)
      .join('path')
      .attr('fill', 'none')
      .attr('stroke-width', 2)
      .attr('stroke-linejoin', 'round')
      .attr('stroke-linecap', 'round')
      .attr('class', (d, i) => {
        if (d.key.includes('Without')) {
          return `stacked-line-without ${i === 1 ? 'visible' : ''}`;
        }

        return `stacked-line ${i === 4 ? 'visible' : ''}`;
      })
      .attr('d', line as any);

    // Create paths
    this.svg
      .append('g')
      .attr('class', 'stacked-area-paths')
      .selectAll('path')
      .data(series)
      .join('path')
      .attr('class', (d, i) => {
        if (d.key.includes('Without')) {
          return `stacked line${i as number} without`;
        }

        return `stacked line${i as number}`;
      })
      .style('fill', (d, i) => {
        if (i === 3 || i === 0) {
          return `transparent`;
        }

        if (d.key.includes('Without')) {
          return `var(--savings-plan-chip-no-monthly-background)`;
        }

        return `var(--savings-plan-chip-background)`;
      })
      .attr('d', this.area);

    // Create y axis
    this.svg.append('g').attr('class', 'yaxis-stacked').call(yAxis);

    // Tooltip
    this.switchSelection(this.currentChip);
  }

  switchSelection(index: number) {
    this.currentChip = index;
    this.highlightSelectedLine();
    this.setTooltipData(index);
  }

  highlightSelectedLine() {
    this.lines.attr('stroke-width', (d, i) => {
      if ((i === 1 && this.currentChip === 1) || (i === 4 && this.currentChip === 0)) {
        return 3;
      }

      return 2;
    });
  }

  setTooltipData(index: number) {
    const tooltip = d3.select('#range-chart-tooltip');
    tooltip.style('transform', `translateY(-50%) translateY(${this.getYCoordinates(index)}px)`);

    if (index === 0) {
      this.tooltipData.loss = this.estimatedValues.bad;
      this.tooltipData.expected = this.estimatedValues.expected;
      this.tooltipData.potential = this.estimatedValues.good;
    } else {
      this.tooltipData.loss = this.estimatedValues.badWithout;
      this.tooltipData.expected = this.estimatedValues.expectedWithout;
      this.tooltipData.potential = this.estimatedValues.goodWithout;
    }
  }

  getYCoordinates(index: number): number {
    const lineIndex = index === 0 ? 4 : 1;
    const path = this.lines.filter((d, i) => i === lineIndex).node();
    const endCoords = path.getPointAtLength(path.getTotalLength());
    return endCoords.y;
  }

  toggleDetails(activity: string): void {
    this.showDetails = !this.showDetails;
    this.trackingService.trackActivity(activity);
  }

  track(chip: number): void {
    this.trackingService.trackActivity(chip === 0 ? '[Chip] Monhtly savings' : '[Chip] No monthly savings');
    this.amplitudeService.trackEvent(`Savings Plan Engaged`, {
      savings_plan_ctas: `${chip === 0 ? 'monthly_deposit_selected' : 'no_monthly_deposit_selected'}`,
      kind: this.currentAccount.kind
    });
  }

  onChangeValue(value: number | null) {
    this.newMonthlyValue = value;
    this.trackingService.trackActivity(`[Button] SP - input: ${this.newMonthlyValue}`);
    this.calculateChartData(this.estimatedPeriod);
  }

  onSetDeposit(value: number) {
    this.newMonthlyValue = value;
    if (this.isMin()) {
      this.trackingService.trackActivity(`[Button] SP deposit: Update deposit ${this.newMonthlyValue}`);
      this.store.dispatch(AppRootActions.updateSpDeposit({ deposit: value }));
    } else {
      this.trackingService.trackActivity(`[Button] SP deposit: Error low investment: ${this.newMonthlyValue}`);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.toastNotificationService.show('critical', this.translateService.instant('invest.reg_min_3'));
    }
  }

  isMin() {
    if (isCustomer(this.currentAccount)) {
      return true;
    } else {
      const minimumInitial = INVESTMENT_LIMITS.initial[this.customer.app_location];
      const minimumMonthly = INVESTMENT_LIMITS.monthly[this.customer.app_location];
      return this.customerInvestment.current_amount >= minimumInitial || this.newMonthlyValue >= minimumMonthly;
    }
  }

  onChangeTab($event: number): void {
    this.changeTab.emit($event);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
